import { signOut } from 'firebase/auth';
import { auth } from './firebase';
import { logoutSession } from './auth';
import { httpClient } from './http-client';
import { normalizePermissionError } from './permission-error';
import { useAuthStore } from '@/app/store/auth.store';
import { sessionNotice } from '@/components/feedback/session-notice.store';
import { toast } from '@/components/feedback/useToast';
import { i18n } from '@/shared/i18n';

let installed = false;

export function setupInterceptors() {
  // Guard against React StrictMode + useEffect re-mount double-install.
  if (installed) return;
  installed = true;

  // Attach the current Firebase ID token (auto-refreshing) on every request. Pre-auth calls (login)
  // run before sign-in, so currentUser is null and no Authorization header is added.
  httpClient.interceptors.request.use(async (config) => {
    const user = auth.currentUser;
    if (user) {
      config.headers.Authorization = `Bearer ${await user.getIdToken()}`;
    }
    // Dev-only locale toggle: ask the BE for English error messages only under `?locale=en`.
    // Deliberately a custom header (not Accept-Language, which browsers auto-send from the OS
    // language and would leak English to real users); absent → BE resolves Korean.
    if (i18n.resolvedLanguage === 'en') {
      config.headers['X-Locale'] = 'en';
    }
    return config;
  });

  httpClient.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        // Read the live-session flag BEFORE we clear it below, so we can tell a mid-session expiry (the
        // user WAS logged in) from a boot /auth/refresh miss (never logged in → stay silent, no toast).
        const wasAuthenticated = useAuthStore.getState().isAuthenticated;
        // Clear the server-side refresh cookie (so a reload can't re-establish a rejected session), drop
        // the Firebase session, and clear local auth so the guards send the user back to /login.
        const tearDownSession = () => {
          void logoutSession();
          void signOut(auth);
          useAuthStore.getState().logout();
        };
        if (error.response?.data?.error?.code === 'SESSION_SUPERSEDED') {
          // Single-session eviction (FEATURE_005): a newer login on another device revoked this session.
          // PRD 00 §8.3 requires an acknowledged 얼럿 here, not a passing toast — the user must learn WHY
          // they were signed out — and Error Standard §7.1 classes a duplicate-session conflict as a
          // Business Logic Error → Modal 필수 (SHCT-622).
          //
          // SHCT-861 — §8.3 orders it `얼럿 후 초기화`: the teardown is handed to the notice host and runs
          // on [확인], so the screen is not swapped out from under the message. Tearing down here made
          // the guards redirect while the modal was still up, which is the half of the requirement that
          // was missing. Nothing is granted by the wait — the session is already revoked server-side.
          sessionNotice.show('SUPERSEDED', tearDownSession);
        } else if (sessionNotice.isPending()) {
          // Collateral from an eviction the user has not acknowledged yet.
          //
          // A superseded session fails EVERY request underneath it, and only the first one is
          // guaranteed to carry `SESSION_SUPERSEDED` — the rest surface as a plain 401. Handling
          // those here as an ordinary expiry undid the whole point of the branch above: the
          // teardown ran immediately, the guards redirected to /login, and the modal was swapped
          // out from under the operator without them pressing anything. SHCT-629 made it fire
          // every time rather than intermittently, because `usePermissionSync` puts a GET
          // /auth/me in flight on every protected navigation.
          //
          // So: swallow it. No toast (the modal already says why), no teardown — the deferred one
          // runs on [확인], exactly once.
        } else {
          if (wasAuthenticated) {
            // A live session's token expired / was invalidated mid-use → friendly "log in again" notice
            // (60-min idle, token expiry). Skipped on a cold boot (wasAuthenticated=false) so the silent
            // refresh-cookie re-establish never flashes a spurious message.
            toast.error(i18n.t('login.errors.sessionExpired', { ns: 'auth' }));
          }
          tearDownSession();
        }
      }

      // SHCT-582 — PRD 21 §7.4 P-4. The server answers an RBAC refusal with `권한이 없습니다.`,
      // which §7.4 lists by name as a 폐기된 변형, and every adapter printed it verbatim — so the
      // one string this ticket asked us to retire was arriving from the API. Rewrite it once,
      // here, on the response path all ~18 adapters share. See ./permission-error.ts for why this
      // keys on the error code rather than on the 403 status.
      normalizePermissionError(error, i18n.t('gate.noPermission.notice', { ns: 'common' }));

      return Promise.reject(error);
    },
  );
}
